import { demoHash } from '../../utils/demoBrand';

/**
 * Synthetic Search Console data for demo brands.
 *
 * Demo brands have no Search Console connection, so the page would otherwise
 * render empty during presentations. Everything here mirrors the shapes the
 * real `/api/search-console/*` endpoints return, so the page itself needs no
 * demo-specific branching beyond choosing this source.
 *
 * Values are derived from a hash of the row key, which keeps them stable across
 * renders and reloads instead of reshuffling on every mount.
 */

const BRAND_TERMS = ['glowy'];

const BRAND_QUERIES = [
  'glowy', 'glowy ayakkabı', 'glowy sneaker', 'glowy outlet', 'glowy indirim',
  'glowy kadın ayakkabı', 'glowy erkek ayakkabı', 'glowy mağaza', 'glowy iade', 'glowy kampanya'
];
const NON_BRAND_QUERIES = [
  'spor ayakkabı', 'koşu ayakkabısı', 'beyaz sneaker', 'yürüyüş ayakkabısı',
  'ortopedik ayakkabı', 'memory foam ayakkabı', 'kadın spor ayakkabı',
  'erkek spor ayakkabı', 'günlük ayakkabı', 'bot modelleri'
];
const BRAND_PAGES = [
  'https://demo.glowytics.com/', 'https://demo.glowytics.com/marka/glowy',
  'https://demo.glowytics.com/kampanya/glowy-indirim', 'https://demo.glowytics.com/magazalar',
  'https://demo.glowytics.com/iade-kosullari'
];
const NON_BRAND_PAGES = [
  'https://demo.glowytics.com/kategori/spor-ayakkabi', 'https://demo.glowytics.com/kategori/kosu',
  'https://demo.glowytics.com/urun/beyaz-sneaker', 'https://demo.glowytics.com/kategori/bot',
  'https://demo.glowytics.com/blog/memory-foam-nedir', 'https://demo.glowytics.com/outlet'
];

// Deterministic value in [min, max] for a seed.
const pick = (seed, min, max) => min + (demoHash(seed) % Math.max(1, Math.round((max - min) * 100))) / 100;

function eachDay(startDate, endDate) {
  const out = [];
  const d = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

function dayMetrics(day) {
  const clicks = Math.round(pick(`c${day}`, 180, 420));
  const impressions = Math.round(pick(`i${day}`, 7000, 15000));
  return {
    date: day,
    clicks,
    impressions,
    ctr: impressions > 0 ? clicks / impressions : 0,
    position: Number(pick(`p${day}`, 5.4, 11.2).toFixed(2))
  };
}

function totalsFor(startDate, endDate, scale = 1) {
  const days = eachDay(startDate, endDate).map(dayMetrics);
  const clicks = Math.round(days.reduce((s, d) => s + d.clicks, 0) * scale);
  const impressions = Math.round(days.reduce((s, d) => s + d.impressions, 0) * scale);
  const position = days.length
    ? Number((days.reduce((s, d) => s + d.position, 0) / days.length).toFixed(2))
    : null;
  const queries = Math.round(pick(`q${startDate}${endDate}`, 4200, 9800) * scale);
  return {
    days,
    agg: { queries, clicks, impressions, ctr: impressions > 0 ? clicks / impressions : 0, position }
  };
}

function shiftYear(dateStr, years) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() - years);
  return d.toISOString().slice(0, 10);
}

function prevRange(startDate, endDate) {
  const s = new Date(`${startDate}T00:00:00Z`);
  const e = new Date(`${endDate}T00:00:00Z`);
  const lastDay = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + 1, 0)).getUTCDate();
  const fullMonth = s.getUTCDate() === 1
    && s.getUTCFullYear() === e.getUTCFullYear() && s.getUTCMonth() === e.getUTCMonth()
    && e.getUTCDate() === lastDay;
  if (fullMonth) {
    return {
      start: new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() - 1, 1)).toISOString().slice(0, 10),
      end: new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), 0)).toISOString().slice(0, 10)
    };
  }
  const days = Math.round((e - s) / 86400000) + 1;
  const end = new Date(s); end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end); start.setUTCDate(start.getUTCDate() - (days - 1));
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

const pctChange = (c, p) => (p === 0 ? (c > 0 ? null : 0) : ((c - p) / p) * 100);

function deltas(cur, base) {
  return {
    queriesAbs: cur.queries - base.queries,
    queries: pctChange(cur.queries, base.queries),
    clicks: pctChange(cur.clicks, base.clicks),
    impressions: pctChange(cur.impressions, base.impressions),
    ctr: pctChange(cur.ctr, base.ctr),
    position: cur.position !== null && base.position !== null ? cur.position - base.position : null
  };
}

export function demoOverview({ startDate, endDate, group }) {
  // Brand traffic is the smaller, higher-converting slice.
  const scale = group === 'BRAND' ? 0.22 : group === 'NON_BRAND' ? 0.78 : 1;
  const cur = totalsFor(startDate, endDate, scale);
  const p = prevRange(startDate, endDate);
  const prev = totalsFor(p.start, p.end, scale).agg;
  const y = { start: shiftYear(startDate, 1), end: shiftYear(endDate, 1) };
  const yoy = totalsFor(y.start, y.end, scale * 0.82).agg;

  return {
    startDate, endDate, group,
    brandTerms: BRAND_TERMS,
    brandTermsAuto: false,
    current: cur.agg,
    previous: { ...prev, range: p, delta: deltas(cur.agg, prev) },
    priorYear: { ...yoy, range: y, delta: deltas(cur.agg, yoy), hasData: true },
    daily: cur.days.map(d => ({
      ...d,
      clicks: Math.round(d.clicks * scale),
      impressions: Math.round(d.impressions * scale)
    })),
    dataCoverage: { minDate: shiftYear(startDate, 2), maxDate: endDate }
  };
}

function tableRows(keys, keyName, seedSuffix, scale) {
  return keys.map((k, i) => {
    const impressions = Math.round(pick(`im${k}${seedSuffix}`, 4000, 60000) * scale / (1 + i * 0.35));
    const clicks = Math.round(impressions * pick(`ct${k}${seedSuffix}`, 0.01, 0.14));
    const prevImpr = Math.round(impressions / pick(`pi${k}${seedSuffix}`, 0.7, 1.4));
    const prevClicks = Math.round(clicks / pick(`pc${k}${seedSuffix}`, 0.7, 1.5));
    return {
      [keyName]: k,
      clicks,
      impressions,
      ctr: impressions > 0 ? clicks / impressions : 0,
      position: Number(pick(`po${k}${seedSuffix}`, 2.1, 24.5).toFixed(2)),
      prevClicks,
      prevImpressions: prevImpr,
      clicksDelta: pctChange(clicks, prevClicks),
      impressionsDelta: pctChange(impressions, prevImpr)
    };
  }).sort((a, b) => b.impressions - a.impressions);
}

export function demoQueries({ startDate, endDate, group, compare }) {
  const keys = group === 'BRAND' ? BRAND_QUERIES : group === 'NON_BRAND' ? NON_BRAND_QUERIES
    : [...BRAND_QUERIES, ...NON_BRAND_QUERIES];
  const rows = tableRows(keys, 'query', `${compare}${startDate}`, group === 'BRAND' ? 0.5 : 1);
  return {
    startDate, endDate, group, compare,
    brandTerms: BRAND_TERMS,
    total: group === 'BRAND' ? 1284 : group === 'NON_BRAND' ? 6431 : 7715,
    page: 1, pageSize: 100, rows
  };
}

export function demoPages({ startDate, endDate, group, compare }) {
  const keys = group === 'BRAND' ? BRAND_PAGES : group === 'NON_BRAND' ? NON_BRAND_PAGES
    : [...BRAND_PAGES, ...NON_BRAND_PAGES];
  const rows = tableRows(keys, 'page', `${compare}${startDate}`, group === 'BRAND' ? 0.6 : 1);
  return {
    startDate, endDate, group, compare,
    brandTerms: BRAND_TERMS,
    source: 'page_query',
    total: group === 'BRAND' ? 96 : group === 'NON_BRAND' ? 214 : 310,
    page: 1, pageSize: 100, rows
  };
}

export function demoBreakdown({ startDate, dimension }) {
  const keys = dimension === 'device'
    ? ['MOBILE', 'DESKTOP', 'TABLET']
    : ['tur', 'deu', 'nld', 'usa', 'gbr', 'fra', 'bel', 'aut'];
  const rows = tableRows(keys, 'key', `bd${startDate}`, dimension === 'device' ? 6 : 1)
    .map(r => ({ key: r.key, clicks: r.clicks, impressions: r.impressions, ctr: r.ctr, position: r.position, clicksDelta: r.clicksDelta, impressionsDelta: r.impressionsDelta }));
  return { dimension, rows, total: rows.length };
}

export function demoMonthly() {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    months.push(d.toISOString().slice(0, 10));
  }
  const base = months.map(m => {
    const clicks = Math.round(pick(`mc${m}`, 5200, 12800));
    const impressions = Math.round(pick(`mi${m}`, 210000, 520000));
    return {
      month: m, clicks, impressions,
      ctr: impressions > 0 ? clicks / impressions : 0,
      position: Number(pick(`mp${m}`, 5.2, 10.8).toFixed(2))
    };
  });
  const byMonth = new Map(base.map(m => [m.month, m]));
  return base.map((m, i) => {
    const prev = base[i + 1] || null;
    const yoy = byMonth.get(`${Number(m.month.slice(0, 4)) - 1}${m.month.slice(4)}`) || null;
    return {
      ...m,
      clicksDelta: prev ? pctChange(m.clicks, prev.clicks) : null,
      impressionsDelta: prev ? pctChange(m.impressions, prev.impressions) : null,
      ctrDelta: prev ? pctChange(m.ctr, prev.ctr) : null,
      positionDelta: prev ? Number((m.position - prev.position).toFixed(2)) : null,
      yoyClicksDelta: yoy ? pctChange(m.clicks, yoy.clicks) : null,
      yoyImpressionsDelta: yoy ? pctChange(m.impressions, yoy.impressions) : null
    };
  });
}
